// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useWidgetBadges from "@insite/client-framework/Common/Hooks/useWidgetBadges";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import { ProductContext, ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import {
    isWishListLineDeactivated,
    isWishListLineDiscontinued,
    isWishListLineRestricted,
} from "@insite/client-framework/Store/Data/WishListLines/WishListLinesSelectors";
import setWishListLineIsSelected from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/SetWishListLineIsSelected";
import updateQuantity from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/UpdateQuantity";
import updateUnitOfMeasure from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/UpdateUnitOfMeasure";
import updateWishListLine from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/UpdateWishListLine";
import updateWishListLineQuantities from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/UpdateWishListLineQuantities";
import translate from "@insite/client-framework/Translate";
import { ProductModel, WishListLineModel, WishListModel } from "@insite/client-framework/Types/ApiModels";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import ProductAddToCartButton from "@insite/content-library/Components/ProductAddToCartButton";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductImageBadges, { ProductImageBadgesStyles } from "@insite/content-library/Components/ProductImageBadges";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import ProductQuantityBreakPricing, {
    ProductQuantityBreakPricingStyles,
} from "@insite/content-library/Components/ProductQuantityBreakPricing";
import ProductTextBadges, { ProductTextBadgesStyles } from "@insite/content-library/Components/ProductTextBadges";
import ProductUnitOfMeasureSelect from "@insite/content-library/Components/ProductUnitOfMeasureSelect";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import MyListDetailsDiscontinuedProductListLine from "@insite/content-library/Widgets/MyListsDetails/MyListDetailsDiscontinuedProductListLine";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxPresentationProps, CheckboxProps } from "@insite/mobius/Checkbox";
import Clickable from "@insite/mobius/Clickable";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import Icon, { IconPresentationProps, IconProps } from "@insite/mobius/Icon";
import Check from "@insite/mobius/Icons/Check";
import RefreshCw from "@insite/mobius/Icons/RefreshCw";
import { LoadingOverlay, TextArea } from "@insite/mobius/index";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import OverflowMenu from "@insite/mobius/OverflowMenu";
import { SelectPresentationProps } from "@insite/mobius/Select";
import TextField, { TextFieldProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Notify from "@tps/App/Components/Notify/Notify";
import ProductQuantityOrdered, { ProductQuantityStyles } from "@tps/Overrides/Components/ProductQuantityOrdered";
import { sharedDateTimeOptions } from "@tps/XDL/constants";
import cloneDeep from "lodash/cloneDeep";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    wishList: WishListModel;
    wishListLine: WishListLineModel;
    productInfo: ProductInfo;
    onDeleteClick: (wishListLine: WishListLineModel) => void;
    onEditNotesClick: (wishListLine: WishListLineModel) => void;
    showImageBadges: boolean;
    maximumNumberOfImageBadges: number;
    showTextBadges: boolean;
    maximumNumberOfTextBadges: number;
    onViewReplacementProduct?: (wishListLine: WishListLineModel) => void;
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    isSelected: !!state.pages.myListDetails.selectedWishListLineIds.find(o => o === ownProps.wishListLine.id),
    settingsCollection: getSettingsCollection(state),
    isQuantityUpdated: state.pages.myListDetails.wishListLinesWithUpdatedQuantity[ownProps.wishListLine.id],
    // XNG-Changes
    editingSortOrder: state.pages.myListDetails.editingSortOrder,
    language: state.context.session.language,
});

const mapDispatchToProps = {
    setWishListLineIsSelected,
    updateUnitOfMeasure,
    updateQuantity,
    updateWishListLineQuantities,
    // XNG-Changes
    updateWishListLine,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface MyListsDetailsProductListLineStyles {
    editNotesLink?: LinkPresentationProps;
    lineInnerContainer?: GridContainerProps;
    selectedCheckboxHidden?: HiddenProps;
    imageGridItem?: GridItemProps;
    selectedCheckbox?: CheckboxPresentationProps;
    imageWrapper?: InjectableCss;
    editNotesHidden?: HiddenProps;
    productImageStyles?: ProductImageStyles;
    descriptionGridItem?: GridItemProps;
    productBrandStyles?: ProductBrandStyles;
    productDescriptionStyles?: ProductDescriptionStyles;
    productPartNumbersStyles?: ProductPartNumbersStyles;
    notesStyles?: TypographyPresentationProps;
    overflowMenuGridItem?: GridItemProps;
    partNumbersHidden?: HiddenProps;
    partNumbersGridItem?: GridItemProps;
    priceAndAvailabilityGridItem?: GridItemProps;
    priceAndAvailabilityInnerContainer?: GridContainerProps;
    purchasedDateGridItem?: GridItemProps;
    purchasedDateLabelText?: TypographyPresentationProps;
    purchasedDateText?: TypographyPresentationProps;
    priceGridItem?: GridItemProps;
    productPriceStyles?: ProductPriceStyles;
    quantityBreakPricing?: ProductQuantityBreakPricingStyles;
    uomAndQuantityGridItem?: GridItemProps;
    uomAndQuantityInnerContainer?: GridContainerProps;
    uomGridItem?: GridItemProps;
    productUnitOfMeasureSelectStyles?: SelectPresentationProps;
    quantityGridItem?: GridItemProps;
    productQuantityOrderedStyles?: ProductQuantityStyles;
    availabilityGridItem?: GridItemProps;
    productAvailabilityStyles?: ProductAvailabilityStyles;
    notesHidden?: HiddenProps;
    notesGridItem?: GridItemProps;
    buttonsGridItem?: GridItemProps;
    addToCartButton?: ButtonPresentationProps;
    deleteLinkHidden?: HiddenProps;
    deleteLink?: LinkPresentationProps;
    updateQuantityWrapper?: InjectableCss;
    updateQuantityLink?: LinkPresentationProps;
    quantityUpdatedIcon?: IconPresentationProps;
    quantityUpdatedText?: TypographyPresentationProps;
    restrictedDescriptionGridItem?: GridItemProps;
    restrictedDescriptionText?: TypographyPresentationProps;
    restrictedMessageText?: TypographyPresentationProps;
    restrictedRemoveItemLink?: LinkPresentationProps;
    textBadgeStyles?: ProductTextBadgesStyles;
    imageBadgeStyles?: ProductImageBadgesStyles;
    // XNG-Changes
    sortOrderGridItem?: GridItemProps;
    sortOrderTextField?: TextFieldProps;
    imageSection?: InjectableCss;
    infoContainer?: GridContainerProps;
    productDetailsContainer?: GridContainerProps;
    container?: InjectableCss;
    info?: GridItemProps;
    priceAndActions?: GridItemProps;
    addToCartWrapper?: InjectableCss;
    quantityAddtoCartWrapper?: InjectableCss;
    addNotesWrapper?: GridItemProps;
    brandGridItem?: GridItemProps;
    restrictedMessageGridItem?: GridItemProps;
    removeItemWrapper?: InjectableCss;
    removeItemLink?: IconProps;
    emptySpaceWrapper?: InjectableCss;
    actions?: InjectableCss;
    restrictedRemoveItemLinkWrapper?: InjectableCss;
    addToCartActionsWrapper?: InjectableCss;
    quantityUpdatedWrapper?: InjectableCss;
    removeItemLinkDesktop?: LinkPresentationProps;
    actionsWrapper?: InjectableCss;
    lineWrapper?: InjectableCss;
    infoGridItem?: GridItemProps;
    infoGridContainer?: GridContainerProps;
    leftInfoGridItem?: GridItemProps;
    rightInfoGridItem?: GridItemProps;
    pricingGridItem?: GridItemProps;
    actionsGridItem?: GridItemProps;
    showMoreLink?: LinkPresentationProps;
    createdOnText?: TypographyPresentationProps;
}

export const productListLineStyles: MyListsDetailsProductListLineStyles = {
    editNotesLink: {
        typographyProps: { size: 12 },
        css: css`
            margin-top: 5px;
        `,
    },
    lineInnerContainer: {
        gap: 10,
    },
    selectedCheckboxHidden: { below: "md" },
    imageGridItem: {
        width: [3, 3, 2, 2, 1],
        printWidth: 1,
    },
    selectedCheckbox: {
        sizeVariant: "small",
        css: css`
            @media print {
                display: none;
            }
            margin-right: 10px;
        `,
    },
    imageWrapper: {
        css: css`
            @media print {
                max-height: 83px;
                max-width: 83px;
            }
            width: 100%;
            min-width: 0;
            flex-direction: column;
        `,
    },
    editNotesHidden: { below: "md" },
    descriptionGridItem: {
        width: [8, 8, 4, 4, 4],
        printWidth: 6,
        css: css`
            flex-direction: column;
        `,
    },
    productPartNumbersStyles: {
        erpNumberGridItem: {
            css: css`
                flex-direction: column;
            `,
        },
        erpNumberLabelText: { variant: "legend" },
        customerNameGridItem: {
            css: css`
                flex-direction: column;
            `,
        },
        customerNameLabelText: { variant: "legend" },
        manufacturerItemGridItem: {
            css: css`
                flex-direction: column;
            `,
        },
        manufacturerItemLabelText: { variant: "legend" },
    },
    overflowMenuGridItem: {
        width: [1, 1, 0, 0, 0],
        printWidth: 0,
    },
    partNumbersHidden: {
        below: "md",
        css: css`
            @media print {
                display: block;
            }
            width: 100%;
        `,
    },
    partNumbersGridItem: { width: 12 },
    priceAndAvailabilityGridItem: {
        width: [12, 12, 4, 4, 5],
        printWidth: 5,
    },
    priceAndAvailabilityInnerContainer: { gap: 10 },
    purchasedDateGridItem: {
        width: [12, 12, 12, 12, 5],
        printWidth: 5,
        css: css`
            flex-direction: column;
        `,
    },
    purchasedDateLabelText: {
        variant: "p",
        transform: "uppercase",
        weight: 600,
        css: css`
            @media print {
                font-size: 12px;
            }
        `,
    },
    priceGridItem: {
        width: [12, 12, 12, 12, 5],
        printWidth: 5,
        css: css`
            flex-direction: column;
        `,
    },
    productPriceStyles: {
        priceLabelText: {
            variant: "p",
            transform: "uppercase",
            weight: 600,
            css: css`
                @media print {
                    font-size: 12px;
                }
            `,
        },
    },
    quantityBreakPricing: {
        viewLink: { typographyProps: { size: 12 } },
    },
    uomAndQuantityGridItem: {
        width: [12, 12, 12, 12, 7],
        printWidth: 7,
    },
    uomAndQuantityInnerContainer: {
        gap: 10,
        css: css`
            @media print {
                input,
                select {
                    height: 18px !important;
                    font-size: 11px !important;
                }
            }
        `,
    },
    uomGridItem: { width: 9, printWidth: 6 },
    quantityGridItem: { width: 3, printWidth: 6 },
    // productQuantityOrderedStyles: {
    //     labelProps: {
    //         css: css`
    //             @media print {
    //                 font-size: 12px;
    //             }
    //         `,
    //     },
    //     cssOverrides: {
    //         inputSelect: css`
    //             @media print {
    //                 width: 100px;
    //             }
    //         `,
    //     },
    // },
    productUnitOfMeasureSelectStyles: {
        labelProps: {
            css: css`
                @media print {
                    font-size: 12px;
                }
            `,
        },
        iconProps: {
            css: css`
                @media print {
                    display: none;
                }
            `,
        },
    },
    availabilityGridItem: { width: 12 },
    buttonsGridItem: {
        width: [12, 12, 2, 2, 2],
        printWidth: 0,
        css: css`
            flex-direction: column;
            align-items: center;
        `,
    },
    notesHidden: {
        above: "sm",
        css: css`
            @media print {
                display: none;
            }
            width: 100%;
        `,
    },
    notesGridItem: { width: 12 },
    addToCartButton: {
        css: css`
            width: 100%;
        `,
    },
    deleteLinkHidden: {
        below: "md",
        css: css`
            display: flex;
            width: 100%;
            flex-direction: column;
            align-items: center;
        `,
    },
    deleteLink: {
        typographyProps: { size: 12 },
        css: css`
            margin-top: 10px;
        `,
    },
    updateQuantityWrapper: {
        css: css`
            display: flex;
            margin-top: 10px;
        `,
    },
    updateQuantityLink: {
        typographyProps: { size: 12 },
        icon: {
            iconProps: {
                src: RefreshCw,
                size: 12,
            },
        },
    },
    quantityUpdatedIcon: {
        src: Check,
        size: 16,
        css: css`
            margin-right: 5px;
        `,
    },
    quantityUpdatedText: {
        size: 12,
    },
    restrictedDescriptionGridItem: {
        width: [12, 12, 6, 6, 5],
        printWidth: 5,
        css: css`
            flex-direction: column;
        `,
    },
    restrictedMessageText: { color: "warning" },

    textBadgeStyles: {
        container: {
            css: css`
                width: 60%;
                margin-bottom: 5px;
            `,
        },
    },
    imageBadgeStyles: {
        container: {
            css: css`
                width: 60%;
                margin-bottom: 5px;
            `,
        },
        imageItem: {
            css: css`
                margin: 5px 0;
            `,
        },
    },
};

const styles = productListLineStyles;

const MyListsDetailsProductListLine: React.FC<Props> = ({
    wishList,
    wishListLine,
    productInfo,
    isSelected,
    settingsCollection,
    isQuantityUpdated,
    onDeleteClick,
    onEditNotesClick,
    updateWishListLineQuantities,
    setWishListLineIsSelected,
    updateUnitOfMeasure,
    updateQuantity,
    showImageBadges,
    showTextBadges,
    maximumNumberOfImageBadges,
    maximumNumberOfTextBadges,
    onViewReplacementProduct,
    // XNG-Changes
    editingSortOrder,
    updateWishListLine,
    language,
}) => {
    const product = mapWishListLineToProduct(wishListLine);
    const [showMore, setShowMore] = useState(wishListLine.notes.length <= 100);

    const selectChangeHandler: CheckboxProps["onChange"] = (_, value) => {
        setWishListLineIsSelected({ wishListLineId: wishListLine.id, isSelected: value });
    };

    const onUpdateQuantityClick = () => {
        updateWishListLineQuantities({ reloadWishListLines: true });
    };

    // XNG-Changes: add spinner when changing UOM
    const hideLoader = () => {
        setIsLoading(false);
    };

    const canEditWishList = (wishList.allowEdit || !wishList.isSharedList) && !wishList.isAutogenerated;

    const { productSettings } = settingsCollection;
    const productContext: ProductContextModel = {
        product,
        productInfo,
        onQtyOrderedChanged: qtyOrdered => {
            updateQuantity({ wishListLineId: wishListLine.id, qtyOrdered });
        },
        onUnitOfMeasureChanged: unitOfMeasure => {
            // XNG-Changes: add spinner when changing UOM
            setIsLoading(true);
            updateUnitOfMeasure({
                wishListLineId: wishListLine.id,
                unitOfMeasure,
                // XNG-Changes: add spinner when changing UOM
                onSuccess: hideLoader,
                onComplete: hideLoader,
            });
        },
    };

    const { imageBadges, textBadges } = useWidgetBadges({ productContext, showTextBadges, showImageBadges });

    // XNG-Changes: add spinner when changing UOM
    const [isLoading, setIsLoading] = React.useState(false);

    // Xng-changes: refactor disabled lines
    const isRestricted = isWishListLineRestricted(wishListLine, productInfo);
    const isDiscontinued = isWishListLineDiscontinued(wishListLine, productInfo);

    // XNG-Changes - start : sort order moved to this file
    const updateSortOrder = (wishListLine: WishListLineModel, value: number | string) => {
        const sortOrder = parseInt(value.toString() || "1", 10);
        if (wishListLine.sortOrder === sortOrder) {
            return;
        }
        const wishListLineToUpdate = { ...wishListLine, sortOrder };
        updateWishListLine({
            wishListId: wishList.id,
            wishListLineId: wishListLineToUpdate.id,
            wishListLine: wishListLineToUpdate,
        });
    };

    const sortOrderBlurHandler = (event: React.FocusEvent<HTMLInputElement>) => {
        updateSortOrder(wishListLine, event.target.value);
    };

    const sortOrderKeyPressHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.which === 13) {
            (event.target as any).blur();
        }
    };

    if (isWishListLineDiscontinued(wishListLine, productInfo) && wishListLine.isActive) {
        return (
            <MyListDetailsDiscontinuedProductListLine
                wishList={wishList}
                wishListLine={wishListLine}
                productContext={productContext}
                isSelected={isSelected}
                selectChangeHandler={selectChangeHandler}
                onDeleteClick={onDeleteClick}
                onViewReplacementProduct={onViewReplacementProduct}
            />
        );
    }

    // XNG-Changes: Complete structure and styles change
    return (
        <LoadingOverlay loading={isLoading}>
            <ProductContext.Provider value={productContext}>
                <StyledWrapper
                    data-test-selector={`${wishListLine.productId}_${wishListLine.selectedUnitOfMeasure}`}
                    {...styles.container}
                >
                    <StyledWrapper {...styles.actionsWrapper}>
                        <Checkbox
                            {...styles.selectedCheckbox}
                            checked={isSelected}
                            onChange={selectChangeHandler}
                            data-test-selector="myListDetails_selectRowCheckbox"
                        ></Checkbox>
                        <Link
                            {...styles.deleteLink}
                            onClick={() => onDeleteClick(wishListLine)}
                            data-test-selector="deleteListItem"
                        ></Link>
                    </StyledWrapper>
                    <StyledWrapper {...styles.lineWrapper}>
                        <StyledWrapper {...styles.imageSection}>
                            <ProductImage product={productContext} extendedStyles={styles.productImageStyles} />
                        </StyledWrapper>
                        <GridContainer {...styles.infoContainer}>
                            <GridItem {...styles.info}>
                                <GridContainer {...styles.productDetailsContainer}>
                                    {product.brand && (
                                        <GridItem {...styles.brandGridItem}>
                                            <ProductBrand
                                                brand={product.brand}
                                                extendedStyles={styles.productBrandStyles}
                                            />
                                        </GridItem>
                                    )}
                                    <GridItem {...styles.descriptionGridItem}>
                                        <ProductDescription
                                            product={productContext}
                                            extendedStyles={styles.productDescriptionStyles}
                                        />
                                    </GridItem>
                                    <GridItem {...styles.infoGridItem}>
                                        <GridContainer {...styles.infoGridContainer}>
                                            <GridItem {...styles.leftInfoGridItem}>
                                                <StyledWrapper {...styles.partNumbersGridItem}>
                                                    <ProductPartNumbers
                                                        productNumber={product.productNumber}
                                                        customerProductNumber={product.customerProductNumber}
                                                        manufacturerItem={product.manufacturerItem}
                                                        extendedStyles={styles.productPartNumbersStyles}
                                                    />
                                                </StyledWrapper>
                                                <Typography {...styles.createdOnText}>
                                                    {translate("Added")}{" "}
                                                    {getLocalizedDateTime({
                                                        dateTime: new Date(wishListLine.createdOn),
                                                        language,
                                                        options: {
                                                            ...sharedDateTimeOptions,
                                                        },
                                                    })}
                                                </Typography>
                                                {editingSortOrder && (
                                                    <StyledWrapper {...styles.sortOrderGridItem}>
                                                        <TextField
                                                            {...styles.sortOrderTextField}
                                                            label={translate("Sort Order")}
                                                            type="number"
                                                            min={1}
                                                            defaultValue={wishListLine.sortOrder}
                                                            onBlur={sortOrderBlurHandler}
                                                            onKeyPress={sortOrderKeyPressHandler}
                                                        />
                                                    </StyledWrapper>
                                                )}
                                                {showTextBadges && textBadges && textBadges.length > 0 && (
                                                    <ProductTextBadges
                                                        badgeList={textBadges}
                                                        maxBadges={maximumNumberOfTextBadges ?? 4}
                                                        extendedStyles={styles.textBadgeStyles}
                                                    />
                                                )}
                                                {showImageBadges && imageBadges && imageBadges.length > 0 && (
                                                    <ProductImageBadges
                                                        badgeList={imageBadges}
                                                        maxBadges={maximumNumberOfImageBadges ?? 4}
                                                        extendedStyles={styles.imageBadgeStyles}
                                                        numberOfColumns={4}
                                                    />
                                                )}
                                            </GridItem>
                                            {productSettings.showInventoryAvailability && wishListLine.trackInventory && (
                                                <GridItem {...styles.rightInfoGridItem}>
                                                    <ProductContextAvailability
                                                        extendedStyles={styles.productAvailabilityStyles}
                                                    />
                                                </GridItem>
                                            )}
                                        </GridContainer>
                                    </GridItem>
                                </GridContainer>
                            </GridItem>
                            <GridItem {...styles.priceAndActions}>
                                <GridItem {...styles.notesGridItem}>
                                    {canEditWishList && (
                                        <Link
                                            {...styles.editNotesLink}
                                            onClick={() => onEditNotesClick(wishListLine)}
                                            data-test-selector="myListDetails_addOrEditNote"
                                            aria-label={translate(`${wishListLine.notes ? "Edit" : "Add"} Notes`)}
                                        >
                                            {translate(`${wishListLine.notes ? "Edit" : "Add"} Notes`)}
                                        </Link>
                                    )}
                                    {wishListLine.notes && (
                                        <Typography {...styles.notesStyles}>
                                            {showMore ? wishListLine.notes : `${wishListLine.notes.slice(0, 100)}...`}
                                        </Typography>
                                    )}
                                    {wishListLine.notes && wishListLine.notes.length > 100 && (
                                        <Link {...styles.showMoreLink} onClick={() => setShowMore(!showMore)}>
                                            {translate("Show More")}
                                        </Link>
                                    )}
                                </GridItem>
                                <GridItem {...styles.actionsGridItem}>
                                    <StyledWrapper {...styles.addToCartWrapper}>
                                        {!isRestricted && !isDiscontinued && (
                                            <>
                                                {wishList.isAutogenerated && (
                                                    <GridItem {...styles.purchasedDateGridItem}>
                                                        <Typography {...styles.purchasedDateLabelText}>
                                                            {translate("Purchased Date")}
                                                        </Typography>
                                                        <Typography {...styles.purchasedDateText}>
                                                            <LocalizedDateTime dateTime={wishListLine.createdOn} />
                                                        </Typography>
                                                    </GridItem>
                                                )}

                                                <ProductPrice
                                                    product={productContext}
                                                    showSavings={true}
                                                    showSavingsAmount={productSettings.showSavingsAmount}
                                                    showSavingsPercent={productSettings.showSavingsPercent}
                                                    showLabel={false}
                                                    extendedStyles={styles.productPriceStyles}
                                                />
                                                <ProductQuantityBreakPricing
                                                    extendedStyles={styles.quantityBreakPricing}
                                                />
                                            </>
                                        )}
                                        <StyledWrapper {...styles.quantityAddtoCartWrapper}>
                                            <ProductQuantityOrdered
                                                disabled={isRestricted || isDiscontinued}
                                                extendedStyles={styles.productQuantityOrderedStyles}
                                            />
                                            {product.unitOfMeasures && product.unitOfMeasures.length > 1 && (
                                                <ProductUnitOfMeasureSelect
                                                    disabled={!canEditWishList || isRestricted || isDiscontinued}
                                                    extendedStyles={styles.productUnitOfMeasureSelectStyles}
                                                />
                                            )}
                                            <ProductAddToCartButton
                                                notes={wishListLine.notes}
                                                extendedStyles={styles.addToCartButton}
                                                data-test-selector="addToCart"
                                                {...{ disabled: isRestricted || isDiscontinued }}
                                            />
                                        </StyledWrapper>

                                        {!isRestricted && !isDiscontinued && canEditWishList && (
                                            <>
                                                {productInfo.qtyOrdered > 0 &&
                                                    wishListLine.qtyOrdered !== productInfo.qtyOrdered && (
                                                        <Link
                                                            {...styles.updateQuantityLink}
                                                            onClick={() => onUpdateQuantityClick()}
                                                        >
                                                            {translate("Update QTY")}
                                                        </Link>
                                                    )}
                                                {isQuantityUpdated &&
                                                    wishListLine.qtyOrdered === productInfo.qtyOrdered && (
                                                        <StyledWrapper {...styles.quantityUpdatedWrapper}>
                                                            <Icon {...styles.quantityUpdatedIcon} />
                                                            <Typography {...styles.quantityUpdatedText}>
                                                                {translate("QTY updated")}
                                                            </Typography>
                                                        </StyledWrapper>
                                                    )}
                                            </>
                                        )}
                                    </StyledWrapper>
                                </GridItem>
                            </GridItem>
                            {isRestricted && !isWishListLineDeactivated(wishListLine) && (
                                <Notify type="info">
                                    {siteMessage("Lists_Item_Not_Displayed_Due_To_Restrictions")}
                                </Notify>
                            )}
                            {isDiscontinued && (
                                <Notify type="info">
                                    {siteMessage("Lists_Item_Not_Displayed_Due_To_Discontinued")}
                                </Notify>
                            )}
                            {isWishListLineDeactivated(wishListLine) && (
                                <Notify type="info">
                                    {siteMessage("Lists_Item_Not_Displayed_Due_To_Deactivation")}
                                </Notify>
                            )}
                        </GridContainer>
                    </StyledWrapper>
                </StyledWrapper>
            </ProductContext.Provider>
        </LoadingOverlay>
    );
};

const mapWishListLineToProduct = (wishListLine: WishListLineModel) => {
    const product = cloneDeep(wishListLine) as any as ProductModel;
    product.id = wishListLine.productId;
    product.productNumber = wishListLine.erpNumber;
    product.productTitle = wishListLine.shortDescription;
    product.customerProductNumber = wishListLine.customerName;
    product.unitOfMeasures =
        wishListLine?.productUnitOfMeasures?.map(u => ({
            id: u.productUnitOfMeasureId,
            ...u,
        })) || null;
    return product;
};

export default connect(mapStateToProps, mapDispatchToProps)(MyListsDetailsProductListLine);
